import type { paths } from '@bet99-pickem/api-types';

export type ApiFnReturnType<FnType extends (...args: any) => Promise<any>> = Awaited<
  ReturnType<FnType>
>;

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

export type RequestBody<Path extends keyof paths, Method extends HttpMethod> = paths[Path] extends {
  [K in Method]: { requestBody?: { content: { 'application/json': infer Body } } };
}
  ? Body
  : never;

export type ResponseBody<
  Path extends keyof paths,
  Method extends HttpMethod,
> = paths[Path] extends {
  [K in Method]: { responses: { 200: { content: { 'application/json': infer Body } } } };
}
  ? Body
  : paths[Path] extends {
        [K in Method]: { responses: { 201: { content: { 'application/json': infer Body } } } };
      }
    ? Body
    : unknown;

// Legacy type names for backward compatibility
export type ExtractRequestBody<
  Path extends keyof paths,
  Method extends keyof paths[Path],
> = RequestBody<Path, Method extends HttpMethod ? Method : never>;

export type ExtractResponseBody<
  Path extends keyof paths,
  Method extends keyof paths[Path],
> = ResponseBody<Path, Method extends HttpMethod ? Method : never>;

export const queryKeys = {
  pickems: {
    all: ['pickems'] as const,
    lists: () => [...queryKeys.pickems.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.pickems.lists(), filters] as const,
    details: () => [...queryKeys.pickems.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.pickems.details(), id] as const,
    entries: (id: string) => [...queryKeys.pickems.detail(id), 'entries'] as const,
    userSelections: (pickemId: string, userId: string) =>
      [...queryKeys.pickems.detail(pickemId), 'user-selections', userId] as const,
    scoringStatus: ['pickems', 'scoring-status'] as const,
  },
  questions: {
    all: ['questions'] as const,
    lists: () => [...queryKeys.questions.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.questions.lists(), filters] as const,
    details: () => [...queryKeys.questions.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.questions.details(), id] as const,
  },
  leagues: {
    all: ['leagues'] as const,
    lists: () => [...queryKeys.leagues.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.leagues.lists(), filters] as const,
    details: () => [...queryKeys.leagues.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.leagues.details(), id] as const,
  },
  leaderboard: {
    all: ['leaderboard'] as const,
    pickem: (id: string) => [...queryKeys.leaderboard.all, 'pickem', id] as const,
  },
  settings: {
    all: ['settings'] as const,
    sports: () => [...queryKeys.settings.all, 'sports'] as const,
  },
  devData: {
    all: ['dev-data'] as const,
  },
} as const;
